import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { SlidersHorizontal, Loader2, Save, RotateCcw, Lock, ChevronRight } from 'lucide-react';

import {
    fetchConfigForList,
    fetchCodeTypeList,
    fetchAllCodeList,
    fetchExistPendingStatus,
    fetchConfigGrid,
    submitPageLevelConfig,
    clearCodeTypeAndBelow,
    clearAllCodeList,
    clearPLCSaveResult,
    selectConfigForList,
    selectCodeTypeList,
    selectAllCodeList,
    selectGridList,
    selectIsLocked,
    selectPLCSaveResult,
    selectConfigForLoading,
    selectCodeTypeLoading,
    selectAllCodeLoading,
    selectGridLoading,
    selectLockCheckLoading,
    selectPLCSaveLoading,
    selectPLCSaveError,
    selectGridError,
} from '../../slices/accountsSlice/pageLevelConfigSlice';
import { showToast } from '../../utilities/toastUtilities';

const PLACEHOLDER_MESSAGES = [
    'Please select DCA before SDCA',
    'Please select SDCA before IT',
];

const PageLevelConfig = () => {
    const dispatch = useDispatch();
    const { userData, userDetails } = useSelector((s) => s.auth);
    const currentUser = userData?.userName || userDetails?.userName || 'system';
    const roleId = userData?.roleId || userData?.RID || 0;

    const configForList = useSelector(selectConfigForList);
    const codeTypeList  = useSelector(selectCodeTypeList);
    const allCodeList   = useSelector(selectAllCodeList);
    const gridList       = useSelector(selectGridList);
    const isLocked       = useSelector(selectIsLocked);
    const saveResult     = useSelector(selectPLCSaveResult);

    const configForLoading = useSelector(selectConfigForLoading);
    const codeTypeLoading  = useSelector(selectCodeTypeLoading);
    const allCodeLoading   = useSelector(selectAllCodeLoading);
    const gridLoading      = useSelector(selectGridLoading);
    const gridError        = useSelector(selectGridError);
    const lockCheckLoading = useSelector(selectLockCheckLoading);
    const saveLoading      = useSelector(selectPLCSaveLoading);
    const saveError        = useSelector(selectPLCSaveError);

    const [configFor, setConfigFor] = useState('');
    const [codeTypeValue, setCodeTypeValue] = useState('');
    const [codeTypeText, setCodeTypeText]   = useState('');
    const [allCode, setAllCode] = useState('');

    useEffect(() => {
        dispatch(fetchConfigForList());
    }, [dispatch]);

    useEffect(() => {
        if (saveResult) {
            showToast('success', 'Configuration saved successfully.');
            dispatch(clearPLCSaveResult());
            dispatch(clearCodeTypeAndBelow());
            setCodeTypeValue('');
            setCodeTypeText('');
            setAllCode('');
            if (configFor) dispatch(fetchConfigGrid(configFor));
        }
        if (saveError) {
            showToast('error', typeof saveError === 'string' ? saveError : 'Failed to save the configuration.');
            dispatch(clearPLCSaveResult());
        }
    }, [saveResult, saveError, dispatch, configFor]);

    const invalidAllCodeMessage = useMemo(() => {
        if (allCodeList.length === 1 && !allCodeList[0].acodeid && PLACEHOLDER_MESSAGES.includes(allCodeList[0].acodevalue)) {
            return allCodeList[0].acodevalue;
        }
        return null;
    }, [allCodeList]);

    const handleConfigForChange = (e) => {
        const value = e.target.value;
        setConfigFor(value);
        setCodeTypeValue('');
        setCodeTypeText('');
        setAllCode('');
        dispatch(clearCodeTypeAndBelow());
        if (value) {
            dispatch(fetchCodeTypeList(value));
            dispatch(fetchConfigGrid(value));
        }
    };

    const handleCodeTypeChange = (e) => {
        const value = e.target.value;
        const text = e.target.options[e.target.selectedIndex]?.text?.trim() || '';
        setCodeTypeValue(value);
        setCodeTypeText(text);
        setAllCode('');
        dispatch(clearAllCodeList());
        if (value) {
            dispatch(fetchAllCodeList({ code: value, codetext: text, codefor: configFor }));
            dispatch(fetchExistPendingStatus({ Code: configFor, AllCodeval: text }));
        }
    };

    const handleReset = () => {
        setCodeTypeValue('');
        setCodeTypeText('');
        setAllCode('');
        dispatch(clearCodeTypeAndBelow());
    };

    const handleSubmit = () => {
        if (!configFor) {
            showToast('error', 'Please select a Configuration For.');
            return;
        }
        if (!codeTypeValue) {
            showToast('error', 'Please select a Code Type.');
            return;
        }
        if (!allCode || invalidAllCodeMessage) {
            showToast('error', 'Please select a valid value for All Code.');
            return;
        }
        if (isLocked) {
            showToast('error', 'This configuration is already set and cannot be modified here.');
            return;
        }

        dispatch(submitPageLevelConfig({
            configfor: configFor,
            Code:      codeTypeText,
            CodeValue: codeTypeValue,
            AllCode:   allCode,
            RoleID:    roleId,
            Createdby: currentUser,
        }));
    };

    const canSubmit = configFor && codeTypeValue && allCode && !invalidAllCodeMessage && !isLocked;

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50/30 to-purple-50/30 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950 p-4 md:p-6">

            {/* Page Header */}
            <div className="max-w-5xl mx-auto mb-6">
                <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-indigo-600 via-purple-600 to-violet-700 shadow-xl shadow-indigo-500/20 p-6 text-white">
                    <div className="absolute top-0 right-0 w-64 h-64 bg-violet-500 rounded-full -translate-y-1/2 translate-x-1/4 opacity-20 blur-3xl" />
                    <div className="relative flex items-center gap-4">
                        <div className="w-12 h-12 rounded-2xl bg-white/15 backdrop-blur flex items-center justify-center shadow-lg border border-white/20 flex-shrink-0">
                            <SlidersHorizontal className="h-6 w-6 text-white" />
                        </div>
                        <div>
                            <span className="text-xs font-bold text-indigo-200 uppercase tracking-wider bg-white/10 px-2 py-0.5 rounded-full">
                                Configuration
                            </span>
                            <h1 className="text-xl md:text-2xl font-black tracking-tight mt-1">Page Level Configuration</h1>
                            <p className="text-indigo-200 text-sm mt-0.5">
                                One-time system settings — map cost centres, DCA/SDCA/IT codes, roles and approvers to specific business rules.
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-w-5xl mx-auto space-y-4">

                {/* Selection form */}
                <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-4 md:p-5">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {/* Configuration For */}
                        <div>
                            <label className="block text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">
                                Configuration For
                            </label>
                            <select
                                value={configFor}
                                onChange={handleConfigForChange}
                                disabled={configForLoading}
                                className="w-full px-3 py-2.5 rounded-xl border-2 text-sm bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 border-gray-200 dark:border-gray-700 focus:outline-none focus:ring-2 focus:border-indigo-500 focus:ring-indigo-100 dark:focus:ring-indigo-900/30 transition-all disabled:opacity-60"
                            >
                                <option value="">-Please Select-</option>
                                {configForList.map((c) => (
                                    <option key={c.pfconfigid} value={c.pfconfigid}>{c.pfconfigvalue}</option>
                                ))}
                            </select>
                        </div>

                        {/* Code Type */}
                        <div>
                            <label className="block text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">
                                Code Type
                            </label>
                            <select
                                value={codeTypeValue}
                                onChange={handleCodeTypeChange}
                                disabled={!configFor || codeTypeLoading}
                                className="w-full px-3 py-2.5 rounded-xl border-2 text-sm bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 border-gray-200 dark:border-gray-700 focus:outline-none focus:ring-2 focus:border-indigo-500 focus:ring-indigo-100 dark:focus:ring-indigo-900/30 transition-all disabled:opacity-60"
                            >
                                <option value="">-Please Select-</option>
                                {codeTypeList.map((c) => (
                                    <option key={c.codeid} value={c.codeid}>{c.codevalue}</option>
                                ))}
                            </select>
                            {codeTypeLoading && (
                                <p className="text-[11px] text-gray-400 mt-1 flex items-center gap-1">
                                    <Loader2 className="h-3 w-3 animate-spin" /> Loading…
                                </p>
                            )}
                        </div>

                        {/* All Code */}
                        <div>
                            <label className="block text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1.5">
                                All Code
                            </label>
                            <select
                                value={allCode}
                                onChange={(e) => setAllCode(e.target.value)}
                                disabled={!codeTypeValue || allCodeLoading || !!invalidAllCodeMessage}
                                className="w-full px-3 py-2.5 rounded-xl border-2 text-sm bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 border-gray-200 dark:border-gray-700 focus:outline-none focus:ring-2 focus:border-indigo-500 focus:ring-indigo-100 dark:focus:ring-indigo-900/30 transition-all disabled:opacity-60"
                            >
                                <option value="">-Please Select-</option>
                                {!invalidAllCodeMessage && allCodeList.map((c) => (
                                    <option key={c.acodeid} value={c.acodeid}>{c.acodevalue}</option>
                                ))}
                            </select>
                            {allCodeLoading && (
                                <p className="text-[11px] text-gray-400 mt-1 flex items-center gap-1">
                                    <Loader2 className="h-3 w-3 animate-spin" /> Loading…
                                </p>
                            )}
                            {invalidAllCodeMessage && (
                                <p className="text-[11px] text-amber-600 dark:text-amber-400 mt-1">{invalidAllCodeMessage}</p>
                            )}
                        </div>
                    </div>

                    {lockCheckLoading && (
                        <p className="text-[11px] text-gray-400 mt-3 flex items-center gap-1">
                            <Loader2 className="h-3 w-3 animate-spin" /> Checking configuration status…
                        </p>
                    )}
                    {!lockCheckLoading && isLocked && codeTypeValue && (
                        <div className="mt-3 flex items-center gap-2 rounded-xl border border-amber-200 dark:border-amber-800/60 bg-amber-50 dark:bg-amber-900/10 px-3 py-2 text-xs font-semibold text-amber-700 dark:text-amber-400">
                            <Lock className="h-3.5 w-3.5 flex-shrink-0" />
                            This configuration is already set and cannot be modified here.
                        </div>
                    )}

                    <div className="flex items-center justify-end gap-2 mt-4">
                        <button
                            onClick={handleReset}
                            disabled={saveLoading}
                            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-60 transition-colors"
                        >
                            <RotateCcw className="h-3.5 w-3.5" /> Reset
                        </button>
                        <button
                            onClick={handleSubmit}
                            disabled={!canSubmit || saveLoading}
                            className="inline-flex items-center gap-1.5 px-5 py-2 rounded-xl text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                            {saveLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
                            Submit
                        </button>
                    </div>
                </div>

                {/* Grid of existing configuration for the selected Configuration For */}
                {configFor && (
                    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
                        <div className="px-4 py-3 bg-gray-50 dark:bg-gray-900/40 border-b border-gray-200 dark:border-gray-700 flex items-center gap-2">
                            <ChevronRight className="h-4 w-4 text-indigo-500" />
                            <h2 className="text-sm font-bold text-gray-700 dark:text-gray-200">
                                Configured Values for "{configForList.find(c => c.pfconfigid === configFor)?.pfconfigvalue || configFor}"
                            </h2>
                        </div>

                        {gridLoading ? (
                            <div className="py-10 flex flex-col items-center justify-center gap-2">
                                <Loader2 className="h-5 w-5 text-indigo-500 animate-spin" />
                                <span className="text-xs text-gray-400">Loading configuration…</span>
                            </div>
                        ) : gridError ? (
                            <div className="py-8 text-center text-sm text-rose-500">{gridError}</div>
                        ) : gridList.length === 0 ? (
                            <div className="py-10 text-center text-gray-400 dark:text-gray-500 text-sm">
                                No configuration saved yet for this category.
                            </div>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="bg-gray-50 dark:bg-gray-900/40 text-left">
                                            <th className="px-4 py-3 text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Configuration For</th>
                                            <th className="px-4 py-3 text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Configuration Category</th>
                                            <th className="px-4 py-3 text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Configuration Value</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-50 dark:divide-gray-700">
                                        {gridList.map((row, idx) => (
                                            <tr key={`${row.Configcategories}-${idx}`} className="hover:bg-indigo-50/40 dark:hover:bg-indigo-900/10 transition-colors">
                                                <td className="px-4 py-3 text-gray-600 dark:text-gray-300">{row.Configurationfor}</td>
                                                <td className="px-4 py-3 text-gray-600 dark:text-gray-300">{row.Configcategories}</td>
                                                <td className="px-4 py-3 font-semibold text-gray-800 dark:text-gray-100">{row.ConfigValue}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default PageLevelConfig;
